/**
 * Checkout cart loading and discount validation.
 * Encapsulates loadCart and validateCartDiscounts with cart/loading/error state.
 */
import { useState, useCallback, useRef, useEffect } from 'react';

import { updateCart } from '../cart/api/cartApi';
import { getCart } from '../cart/services/getCart';
import { DISCOUNT_ERRORS } from '../discount/constants/discountErrorMapper';

/**
 * @param {Object} options
 * @param {Function} options.navigate - react-router navigate
 * @param {string} options.eventKey - route event key
 * @param {Function} [options.setClientSecret] - called when payment intent should be reset (e.g. invalid discount)
 * @param {Function} [options.setPiId] - called when payment intent should be reset
 * @param {Function} [options.setDiscountError] - called when discount error message should be set
 */
export function useCheckoutCart({ navigate, eventKey, setClientSecret, setPiId, setDiscountError }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Stable ref relay for setDiscountError.
   *
   * setDiscountError is a useState setter from the parent — React guarantees
   * it is stable, but ESLint cannot prove that through a function parameter.
   * Storing it in a ref gives useCallback a stable reference while always
   * calling the latest version of the function.
   */
  const setDiscountErrorRef = useRef(setDiscountError);
  useEffect(() => { setDiscountErrorRef.current = setDiscountError; }, [setDiscountError]);

  const loadCart = useCallback(async (retryCount = 0) => {
    try {
      setLoading(true);
      const cartData = await getCart();

      if (!cartData || !cartData.lineItems || cartData.lineItems.length === 0) {
        const justLoggedIn = sessionStorage.getItem('justLoggedIn');

        if (justLoggedIn && retryCount === 0) {
          sessionStorage.removeItem('justLoggedIn');
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return loadCart(1);
        }

        navigate(`/${eventKey}`);
        return;
      }

      let finalCart = cartData;
      if (cartData.discountCodes && cartData.discountCodes.length > 0) {
        try {
          const recalculatedCart = await updateCart({
            action: 'recalculate',
            updateProductData: true,
          });
          if (recalculatedCart) {
            if (cartData.totalPrice?.centAmount !== recalculatedCart.totalPrice?.centAmount) {
              if (setClientSecret) setClientSecret(null);
              if (setPiId) setPiId(null);
            }
            finalCart = recalculatedCart;

            const invalidCodes =
              recalculatedCart.discountCodes?.filter((dc) => {
                const stateInvalid = dc.state !== 'MatchesCart';
                const objMissing = !dc.discountCode?.obj;
                const isCodeInactive = dc.discountCode?.obj?.isActive === false;
                
                // Check if underlying cart discounts are active
                const cartDiscounts = dc.discountCode?.obj?.cartDiscounts || [];
                const hasActiveCartDiscount = cartDiscounts.length === 0 || 
                  cartDiscounts.some(cd => cd.obj?.isActive !== false);
                
                return stateInvalid || objMissing || isCodeInactive || !hasActiveCartDiscount;
              }) || [];

            if (invalidCodes.length > 0) {
              for (const invalidDc of invalidCodes) {
                if (invalidDc.discountCode?.id) {
                  try {
                    await updateCart({
                      action: 'removeDiscountCode',
                      discountCode: { typeId: 'discount-code', id: invalidDc.discountCode.id },
                    });
                  } catch (removeErr) {
                    console.warn('Failed to remove invalid discount:', removeErr);
                  }
                }
              }

              try {
                const freshCart = await getCart();
                if (freshCart) finalCart = freshCart;
              } catch (reloadErr) {
                console.warn('Failed to reload cart after discount removal:', reloadErr);
              }

              if (setDiscountErrorRef.current) {
                setDiscountErrorRef.current(DISCOUNT_ERRORS.DEACTIVATED);
              }
              if (setClientSecret) setClientSecret(null);
              if (setPiId) setPiId(null);
            }
          }
        } catch (recalcErr) {
          console.warn('Cart recalculation failed:', recalcErr);
        }
      }

      setCart(finalCart);
    } catch (_err) {
      setError('Failed to load cart');
    } finally {
      setLoading(false);
    }
  }, [eventKey, navigate, setClientSecret, setPiId]);

  const validateCartDiscounts = useCallback(async () => {
    try {
      const freshCart = await getCart();
      if (!freshCart) return { shouldStop: true, message: 'Failed to retrieve cart' };

      const recalculatedCart = await updateCart({
        action: 'recalculate',
        updateProductData: true,
      });

      if (!recalculatedCart) return { shouldStop: true, message: 'Failed to recalculate cart' };

      let finalCart = recalculatedCart;
      let hasChanges = false;

      const invalidCodes =
        finalCart.discountCodes?.filter((dc) => {
          const stateInvalid = dc.state !== 'MatchesCart';
          const objMissing = !dc.discountCode?.obj;
          const isCodeInactive = dc.discountCode?.obj?.isActive === false;
          
          // Check if underlying cart discounts are active
          const cartDiscounts = dc.discountCode?.obj?.cartDiscounts || [];
          const hasActiveCartDiscount = cartDiscounts.length === 0 || 
            cartDiscounts.some(cd => cd.obj?.isActive !== false);
          
          return stateInvalid || objMissing || isCodeInactive || !hasActiveCartDiscount;
        }) || [];

      if (invalidCodes.length > 0) {
        for (const invalidDc of invalidCodes) {
          if (invalidDc.discountCode?.id) {
            await updateCart({
              action: 'removeDiscountCode',
              discountCode: { typeId: 'discount-code', id: invalidDc.discountCode.id },
            });
            if (setDiscountErrorRef.current) {
              setDiscountErrorRef.current(DISCOUNT_ERRORS.DEACTIVATED);
            }
          }
        }
        hasChanges = true;

        const cleanedCart = await getCart();
        if (cleanedCart) finalCart = cleanedCart;

        setCart(finalCart);
      }

      if (cart && cart.totalPrice && finalCart.totalPrice) {
        if (cart.totalPrice.centAmount !== finalCart.totalPrice.centAmount) {
          hasChanges = true;
          // We don't set a main banner message here, because if the price changed due to
          // discount removal, the inline discountError is already set. If line item prices
          // changed, handleAddressSave will catch it anyway.
          setCart(finalCart);
        }
      }

      return {
        shouldStop: hasChanges,
        message: '',
        cart: finalCart,
      };
    } catch (err) {
      console.error('[useCheckoutCart] validateCartDiscounts failed:', err);
      return { shouldStop: true, message: 'Failed to validate discounts. Please try again.' };
    }
  }, [cart]);

  return {
    cart,
    loading,
    error,
    setCart,
    setError,
    setLoading,
    loadCart,
    validateCartDiscounts,
  };
}
